import {
  BrainCircuit,
  ChevronDown,
  Database,
  ExternalLink,
  Languages,
  LineChart,
  Menu,
  Moon,
  ServerCog,
  Sun,
  X
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@/shared/contexts';
import { useI18n } from '@/shared/i18n';
import { NAVIGATION_EVENTS } from '@/shared/utils/constants';
import { smoothEase } from '@/shared/utils/motion';

type ServiceItem = {
  label: string;
  description: string;
  path: string;
  icon: LucideIcon | string;
  hoverIcon?: string;
  isExternal?: boolean;
};

type MiddleNavItem = {
  label: string;
  path: string;
  matchPaths?: string[];
};

type ServiceIconProps = {
  icon: LucideIcon | string;
  hoverIcon?: string;
  size: number;
  className?: string;
};

const ServiceIcon = ({
  icon,
  hoverIcon,
  size,
  className = ''
}: ServiceIconProps) => {
  if (typeof icon === 'string') {
    return (
      <span
        className={`relative inline-block shrink-0 ${className}`.trim()}
        style={{
          width: size,
          height: size
        }}
      >
        <img
          src={icon}
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 h-full w-full object-contain transition-opacity duration-200 ${
            hoverIcon ? 'opacity-100 group-hover:opacity-0' : 'opacity-100'
          }`}
        />

        {hoverIcon ? (
          <img
            src={hoverIcon}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-contain opacity-0 transition-opacity duration-200 group-hover:opacity-100"
          />
        ) : null}
      </span>
    );
  }

  const Icon = icon;

  return <Icon size={size} className={className} />;
};

const serviceItemConfig: Omit<ServiceItem, 'label' | 'description'>[] = [
  {
    path: 'https://www.datasliv.com',
    icon: '/images/datasliv.svg',
    hoverIcon: '/images/datasliv-white.svg',
    isExternal: true
  },
  {
    path: '/services/data-engineering',
    icon: ServerCog
  },
  {
    path: '/services/managed-data-platform',
    icon: Database
  },
  {
    path: '/services/business-intelligence',
    icon: LineChart
  },
  {
    path: '/services/ai-machine-learning',
    icon: BrainCircuit
  }
];

const AppNavbar = () => {
  const { theme, setTheme } = useTheme();
  const { language, languages, setLanguage, t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [homeSection, setHomeSection] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const servicesCloseTimeoutRef = useRef<number | null>(null);
  const languageMenuRef = useRef<HTMLDivElement | null>(null);
  const isDarkTheme = theme !== 'light';
  const isHomePage = location.pathname === '/';
  const isServicesPage = location.pathname.startsWith('/services/');
  const isAboutPage =
    location.pathname === '/about' || location.pathname === '/o-nama';
  const isPartnersPage =
    location.pathname === '/partners' || location.pathname === '/partneri';
  const isContactPage =
    location.pathname === '/contact' || location.pathname === '/kontakt';
  const isCompact = isScrolled || (isHomePage && homeSection > 0);
  const prefersReducedMotion = useReducedMotion();
  const serviceItems: ServiceItem[] = serviceItemConfig.map((item, index) => ({
    ...item,
    label: t.common.serviceMenu[index].label,
    description: t.common.serviceMenu[index].description
  }));
  const middleNavItems: MiddleNavItem[] = [
    {
      label: t.common.nav.insights,
      path: '/insights',
      matchPaths: ['/insights', '/blog']
    },
    { label: t.common.nav.cases, path: '/cases' }
  ];
  const menuTransition = prefersReducedMotion
    ? { duration: 0 }
    : { duration: 0.22, ease: smoothEase };

  useEffect(() => {
    let routeScrollContainers: HTMLElement[] = [];

    const updateScrolledState = () => {
      const hasRouteContainerScrolled = routeScrollContainers.some(
        scrollContainer => scrollContainer.scrollTop > 24
      );

      setIsScrolled(window.scrollY > 24 || hasRouteContainerScrolled);
    };

    const syncRouteScrollContainers = () => {
      const nextScrollContainers = Array.from(
        document.querySelectorAll<HTMLElement>('[data-route-scroll-container]')
      );

      const hasSameScrollContainers =
        nextScrollContainers.length === routeScrollContainers.length &&
        nextScrollContainers.every(
          (scrollContainer, index) =>
            scrollContainer === routeScrollContainers[index]
        );

      if (hasSameScrollContainers) {
        updateScrolledState();
        return;
      }

      routeScrollContainers.forEach(scrollContainer => {
        scrollContainer.removeEventListener('scroll', updateScrolledState);
      });

      routeScrollContainers = nextScrollContainers;

      routeScrollContainers.forEach(scrollContainer => {
        scrollContainer.addEventListener('scroll', updateScrolledState, {
          passive: true
        });
      });

      updateScrolledState();
    };

    syncRouteScrollContainers();

    const animationFrame = window.requestAnimationFrame(
      syncRouteScrollContainers
    );
    const observer = new MutationObserver(syncRouteScrollContainers);

    window.addEventListener('scroll', updateScrolledState, { passive: true });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('scroll', updateScrolledState);
      observer.disconnect();

      routeScrollContainers.forEach(scrollContainer => {
        scrollContainer.removeEventListener('scroll', updateScrolledState);
      });
    };
  }, [location.pathname]);

  useEffect(() => {
    const handleHomeSectionChange = (event: Event) => {
      const sectionEvent = event as CustomEvent<{ sectionIndex: number }>;
      setHomeSection(sectionEvent.detail.sectionIndex);
    };

    window.addEventListener(
      NAVIGATION_EVENTS.HOME_SECTION_CHANGE,
      handleHomeSectionChange
    );

    return () => {
      window.removeEventListener(
        NAVIGATION_EVENTS.HOME_SECTION_CHANGE,
        handleHomeSectionChange
      );
    };
  }, []);

  useEffect(() => {
    if (!isHomePage) {
      setHomeSection(0);
      setIsMobileMenuOpen(false);
      setIsMobileServicesOpen(false);
      setIsServicesDropdownOpen(false);
      setIsLanguageMenuOpen(false);
    }
  }, [isHomePage, location.pathname]);

  useEffect(() => {
    if (!isLanguageMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !languageMenuRef.current?.contains(event.target)
      ) {
        setIsLanguageMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsLanguageMenuOpen(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLanguageMenuOpen]);

  useEffect(() => {
    return () => clearServicesCloseTimeout();
  }, []);

  const goHome = () => {
    setIsMobileMenuOpen(false);
    setIsMobileServicesOpen(false);
    setIsLanguageMenuOpen(false);

    if (isHomePage) {
      window.dispatchEvent(
        new CustomEvent(NAVIGATION_EVENTS.HOME_SECTION_CHANGE, {
          detail: { sectionIndex: 0 }
        })
      );
      setHomeSection(0);
      return;
    }

    void navigate('/');
  };

  const toggleTheme = () => {
    setTheme(isDarkTheme ? 'light' : 'dark');
  };

  const clearServicesCloseTimeout = () => {
    if (servicesCloseTimeoutRef.current !== null) {
      window.clearTimeout(servicesCloseTimeoutRef.current);
      servicesCloseTimeoutRef.current = null;
    }
  };

  const openServicesDropdown = () => {
    clearServicesCloseTimeout();
    setIsServicesDropdownOpen(true);
  };

  const closeServicesDropdown = () => {
    clearServicesCloseTimeout();
    setIsServicesDropdownOpen(false);
  };

  const scheduleServicesDropdownClose = () => {
    clearServicesCloseTimeout();
    servicesCloseTimeoutRef.current = window.setTimeout(() => {
      setIsServicesDropdownOpen(false);
      servicesCloseTimeoutRef.current = null;
    }, 160);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileServicesOpen(false);
    setIsLanguageMenuOpen(false);
  };

  const isMiddleNavItemActive = (item: MiddleNavItem) =>
    (item.matchPaths ?? [item.path]).some(matchPath =>
      matchPath === '/insights' || matchPath === '/blog'
        ? location.pathname.startsWith(matchPath)
        : location.pathname === matchPath
    );

  return (
    <motion.header
      className={`fixed left-0 right-0 top-0 z-50 flex justify-center px-3 sm:px-6 ${
        isCompact ? 'pt-2 sm:pt-3' : 'pt-3 sm:pt-5'
      }`}
      onMouseLeave={scheduleServicesDropdownClose}
      initial={prefersReducedMotion ? false : { opacity: 0, y: -14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        prefersReducedMotion
          ? { duration: 0 }
          : { duration: 0.42, ease: smoothEase }
      }
    >
      <nav
        aria-label={t.common.nav.ariaLabel}
        className={`liquid-glass-nav flex w-full max-w-6xl items-center justify-between overflow-visible rounded-full border transition-all duration-300 ${
          isCompact
            ? 'h-14 px-4 sm:h-16 sm:max-w-5xl sm:px-6'
            : 'h-16 px-4 sm:h-[90px] sm:px-10'
        }`}
      >
        <button
          type="button"
          onClick={goHome}
          className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-full transition hover:bg-[#00bbc3]/5"
          aria-label={t.common.nav.homeAriaLabel}
        >
          <img
            src="/logo.png"
            alt=""
            className="h-12 w-12 rounded-xl"
            aria-hidden="true"
          />
        </button>

        {/* DESKTOP LINKOVI SA NOVIM BOJAMA */}
        <div className="hidden items-center gap-1 text-sm font-bold text-text-secondary lg:flex dark:text-white/88">
          <button
            type="button"
            onClick={goHome}
            onMouseEnter={closeServicesDropdown}
            className={`rounded-full px-4 py-3 transition cursor-pointer hover:text-[#007d9b] dark:hover:text-[#00bbc3] ${
              isHomePage
                ? 'bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] px-5 text-white shadow-lg shadow-[#00bbc3]/20 hover:text-white dark:hover:text-white'
                : 'hover:bg-[#00bbc3]/5 dark:hover:bg-[#00bbc3]/10'
            }`}
          >
            {t.common.nav.home}
          </button>

          <div
            className="relative"
            onMouseEnter={openServicesDropdown}
            onMouseLeave={scheduleServicesDropdownClose}
          >
            <button
              type="button"
              aria-expanded={isServicesDropdownOpen}
              className={`flex items-center gap-1.5 cursor-pointer rounded-full px-8 py-3 transition hover:text-[#007d9b] dark:hover:text-[#00bbc3] ${
                isServicesPage || isServicesDropdownOpen
                  ? 'bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] px-5 text-white shadow-lg shadow-[#00bbc3]/20 hover:text-white dark:hover:text-white'
                  : 'hover:bg-[#00bbc3]/5 dark:hover:bg-[#00bbc3]/10'
              }`}
            >
              {t.common.nav.services}{' '}
              <ChevronDown size={15} strokeWidth={2.5} />
            </button>
          </div>

          <Link
            to="/about"
            onMouseEnter={closeServicesDropdown}
            className={`rounded-full px-4 py-3 transition hover:text-[#007d9b] dark:hover:text-[#00bbc3] ${
              isAboutPage
                ? 'bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] px-5 text-white shadow-lg shadow-[#00bbc3]/20 hover:text-white dark:hover:text-white'
                : 'hover:bg-[#00bbc3]/5 dark:hover:bg-[#00bbc3]/10'
            }`}
          >
            {t.common.nav.about}
          </Link>

          <Link
            to="/partners"
            onMouseEnter={closeServicesDropdown}
            className={`rounded-full px-4 py-3 transition hover:text-[#007d9b] dark:hover:text-[#00bbc3] ${
              isPartnersPage
                ? 'bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] px-5 text-white shadow-lg shadow-[#00bbc3]/20 hover:text-white dark:hover:text-white'
                : 'hover:bg-[#00bbc3]/5 dark:hover:bg-[#00bbc3]/10'
            }`}
          >
            {t.common.nav.partners}
          </Link>

          {middleNavItems.map(item => (
            <Link
              key={item.label}
              to={item.path}
              onMouseEnter={closeServicesDropdown}
              className={`rounded-full px-4 py-3 transition hover:text-[#007d9b] dark:hover:text-[#00bbc3] ${
                isMiddleNavItemActive(item)
                  ? 'bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] px-5 text-white shadow-lg shadow-[#00bbc3]/20 hover:text-white dark:hover:text-white'
                  : 'hover:bg-[#00bbc3]/5 dark:hover:bg-[#00bbc3]/10'
              }`}
            >
              {item.label}
            </Link>
          ))}

          <Link
            to="/contact"
            onMouseEnter={closeServicesDropdown}
            className={`rounded-full px-4 py-3 transition hover:text-[#007d9b] dark:hover:text-[#00bbc3] ${
              isContactPage
                ? 'bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] px-5 text-white shadow-lg shadow-[#00bbc3]/20 hover:text-white dark:hover:text-white'
                : 'hover:bg-[#00bbc3]/5 dark:hover:bg-[#00bbc3]/10'
            }`}
          >
            {t.common.nav.contact}
          </Link>
        </div>

        {/* DESNA STRANA: PREKIDAČ TEME I MOBILNO DUGME */}
        <div className="flex items-center gap-3">
          <div ref={languageMenuRef} className="relative hidden md:block">
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={isLanguageMenuOpen}
              aria-label={t.common.nav.languageSelector}
              onClick={() => {
                closeServicesDropdown();
                setIsLanguageMenuOpen(isOpen => !isOpen);
              }}
              className="flex h-9 cursor-pointer items-center gap-1.5 rounded-full border border-border-primary/80 bg-background-card/50 px-2.5 text-[10px] font-black text-text-secondary backdrop-blur-md transition hover:border-[#00bbc3]/40 hover:text-[#007d9b] dark:border-white/10 dark:bg-white/8 dark:text-white/75 dark:hover:text-[#00bbc3]"
            >
              <Languages
                size={14}
                className="text-[#007d9b] dark:text-[#00bbc3]"
              />
              {t.common.languageShortNames[language]}
              <ChevronDown
                size={13}
                strokeWidth={2.6}
                className={`transition-transform ${
                  isLanguageMenuOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            <AnimatePresence>
              {isLanguageMenuOpen ? (
                <motion.div
                  role="menu"
                  aria-label={t.common.nav.languageSelector}
                  className="liquid-glass-panel absolute right-0 top-[calc(100%+0.5rem)] z-[70] w-44 overflow-hidden rounded-[16px] border bg-background-card/92 p-1.5 shadow-[0_24px_80px_rgba(13,24,36,0.22)] dark:bg-[#101b2e]/94"
                  initial={
                    prefersReducedMotion
                      ? false
                      : { opacity: 0, y: -6, scale: 0.98 }
                  }
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={
                    prefersReducedMotion
                      ? { opacity: 0 }
                      : { opacity: 0, y: -6, scale: 0.98 }
                  }
                  transition={menuTransition}
                >
                  {languages.map(languageOption => {
                    const isActiveLanguage = language === languageOption;

                    return (
                      <button
                        key={languageOption}
                        type="button"
                        role="menuitemradio"
                        aria-checked={isActiveLanguage}
                        onClick={() => {
                          setLanguage(languageOption);
                          setIsLanguageMenuOpen(false);
                        }}
                        className={`flex h-10 w-full cursor-pointer items-center justify-between rounded-[12px] px-3 text-left text-xs font-black transition ${
                          isActiveLanguage
                            ? 'bg-[#007d9b] text-white shadow-sm dark:bg-[#00bbc3] dark:text-[#07142b]'
                            : 'text-text-secondary hover:bg-[#00bbc3]/10 hover:text-[#007d9b] dark:text-white/75 dark:hover:text-[#00bbc3]'
                        }`}
                      >
                        <span>{t.common.languageNames[languageOption]}</span>
                        <span className="text-[10px] opacity-75">
                          {t.common.languageShortNames[languageOption]}
                        </span>
                      </button>
                    );
                  })}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>

          <button
            type="button"
            aria-label={t.common.nav.changeTheme}
            onClick={toggleTheme}
            className="hidden h-9 w-[62px] cursor-pointer items-center rounded-full border-2 border-white bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] px-1 shadow-[inset_0_0_0_1px_rgba(255,255,255,.5)] sm:flex"
          >
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-sm transition-transform duration-300 ${
                isDarkTheme ? 'translate-x-6' : 'translate-x-0'
              }`}
            >
              {isDarkTheme ? (
                <Moon size={14} className="text-[#007d9b]" />
              ) : (
                <Sun size={14} className="text-[#007d9b]" />
              )}
            </span>
          </button>

          <button
            type="button"
            aria-label={t.common.nav.openMenu}
            onClick={() => {
              if (isMobileMenuOpen) {
                closeMobileMenu();
                return;
              }

              setIsMobileMenuOpen(true);
            }}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-[#00bbc3]/5 text-text-primary transition-colors hover:text-[#007d9b] lg:hidden dark:bg-white/10 dark:text-white dark:hover:text-[#00bbc3]"
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* ===== DESKTOP DROPDOWN MENI ===== */}
      <AnimatePresence>
        {isServicesDropdownOpen ? (
          <motion.div
            className={`liquid-glass-panel liquid-glass-services-menu absolute z-[60] hidden w-[360px] overflow-hidden rounded-[18px] border p-2 lg:block ${
              isCompact
                ? 'top-[68px] sm:top-[84px]'
                : 'top-[76px] sm:top-[104px]'
            }`}
            style={{ left: 'calc(50% - 235px)' }}
            initial={
              prefersReducedMotion ? false : { opacity: 0, y: -8, scale: 0.98 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={
              prefersReducedMotion
                ? { opacity: 0 }
                : { opacity: 0, y: -8, scale: 0.98 }
            }
            transition={menuTransition}
            onMouseEnter={openServicesDropdown}
            onMouseLeave={scheduleServicesDropdownClose}
          >
            {serviceItems.map(service => {
              const isCustomImageIcon = typeof service.icon === 'string';
              const serviceContent = (
                <>
                  <span
                    className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] transition-all duration-200 ${
                      isCustomImageIcon
                        ? 'bg-transparent group-hover:bg-transparent'
                        : 'bg-[#00bbc3]/10 text-[#007d9b] group-hover:bg-[#007d9b] group-hover:text-white dark:text-[#00bbc3] dark:group-hover:bg-gradient-to-br dark:group-hover:from-[#007d9b] dark:group-hover:to-[#00bbc3] dark:group-hover:text-white'
                    }`}
                  >
                    <ServiceIcon
                      icon={service.icon}
                      hoverIcon={service.hoverIcon}
                      size={isCustomImageIcon ? 36 : 20}
                      className={isCustomImageIcon ? 'rounded-[10px]' : ''}
                    />
                  </span>

                  <span>
                    <span className="flex items-center gap-1.5 font-black group-hover:text-[#007d9b] dark:group-hover:text-[#00bbc3] transition-colors">
                      {service.label}
                      {service.isExternal ? (
                        <ExternalLink
                          size={13}
                          strokeWidth={2.5}
                          className="shrink-0 opacity-70 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100"
                        />
                      ) : null}
                    </span>
                    <span className="mt-1 block text-xs font-medium leading-5 text-[#4e696b] dark:text-white/56">
                      {service.description}
                    </span>
                  </span>
                </>
              );

              return service.isExternal ? (
                <a
                  key={service.label}
                  href={service.path}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={() => setIsServicesDropdownOpen(false)}
                  className="group flex gap-3 rounded-[14px] px-4 py-3.5 text-[#17383a] transition hover:bg-[#00bbc3]/10 dark:text-white/88 dark:hover:bg-[#00bbc3]/10"
                >
                  {serviceContent}
                </a>
              ) : (
                <Link
                  key={service.label}
                  to={service.path}
                  onClick={() => setIsServicesDropdownOpen(false)}
                  className="group flex gap-3 rounded-[14px] px-4 py-3.5 text-[#17383a] transition hover:bg-[#00bbc3]/10 dark:text-white/88 dark:hover:bg-[#00bbc3]/10"
                >
                  {serviceContent}
                </Link>
              );
            })}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* ===== MOBILNI PANEL ===== */}
      <AnimatePresence>
        {isMobileMenuOpen ? (
          <motion.div
            className="liquid-glass-panel absolute left-3 right-3 top-[74px] max-h-[calc(100dvh-88px)] overscroll-contain overflow-y-auto rounded-[18px] border lg:hidden"
            initial={
              prefersReducedMotion ? false : { opacity: 0, y: -8, height: 0 }
            }
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={
              prefersReducedMotion
                ? { opacity: 0 }
                : { opacity: 0, y: -8, height: 0 }
            }
            transition={menuTransition}
          >
            <div className="grid gap-1 p-3">
              <button
                type="button"
                onClick={goHome}
                className={`flex h-11 w-full items-center rounded-[10px] px-4 text-left text-sm font-black transition ${
                  isHomePage
                    ? 'bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] text-white'
                    : 'text-text-primary hover:bg-[#00bbc3]/5 dark:text-white/86 dark:hover:bg-white/8'
                }`}
              >
                {t.common.nav.home}
              </button>

              <button
                type="button"
                onClick={() => setIsMobileServicesOpen(isOpen => !isOpen)}
                className={`flex h-11 w-full items-center justify-between rounded-[10px] px-4 text-left text-sm font-black transition ${
                  isServicesPage || isMobileServicesOpen
                    ? 'bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] text-white'
                    : 'text-text-primary hover:bg-[#00bbc3]/5 dark:text-white/86 dark:hover:bg-white/8'
                }`}
              >
                {t.common.nav.services}
                <ChevronDown
                  size={16}
                  strokeWidth={2.5}
                  className={`transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence initial={false}>
                {isMobileServicesOpen ? (
                  <motion.div
                    className="mt-1 grid gap-1 overflow-hidden rounded-[14px] border border-[#00bbc3]/10 bg-background-card/60 p-2 shadow-inner shadow-[#00bbc3]/5 dark:border-white/10 dark:bg-[#121d31]/78"
                    initial={
                      prefersReducedMotion ? false : { opacity: 0, height: 0 }
                    }
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={
                      prefersReducedMotion
                        ? { opacity: 0 }
                        : { opacity: 0, height: 0 }
                    }
                    transition={menuTransition}
                  >
                    {serviceItems.map(service => {
                      const isCustomImageIcon =
                        typeof service.icon === 'string';
                      const serviceContent = (
                        <>
                          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center">
                            <ServiceIcon
                              icon={service.icon}
                              hoverIcon={service.hoverIcon}
                              size={isCustomImageIcon ? 24 : 18}
                              className={
                                isCustomImageIcon
                                  ? 'rounded-[7px]'
                                  : 'text-[#007d9b] dark:text-[#00bbc3]'
                              }
                            />
                          </span>

                          <span>
                            <span className="flex items-center gap-1.5 transition-colors group-hover:text-[#007d9b] dark:group-hover:text-[#00bbc3]">
                              {service.label}
                              {service.isExternal ? (
                                <ExternalLink
                                  size={12}
                                  strokeWidth={2.5}
                                  className="shrink-0 opacity-70 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100"
                                />
                              ) : null}
                            </span>
                            <span className="mt-0.5 block text-xs font-medium text-text-tertiary dark:text-white/52">
                              {service.description}
                            </span>
                          </span>
                        </>
                      );

                      return service.isExternal ? (
                        <a
                          key={service.label}
                          href={service.path}
                          target="_blank"
                          rel="noreferrer noopener"
                          onClick={closeMobileMenu}
                          className="group flex gap-3 rounded-[12px] px-3 py-3 text-sm font-bold text-[#17383a] transition hover:bg-[#00bbc3]/10 dark:text-white/82 dark:hover:bg-white/8"
                        >
                          {serviceContent}
                        </a>
                      ) : (
                        <Link
                          key={service.label}
                          to={service.path}
                          onClick={closeMobileMenu}
                          className="group flex gap-3 rounded-[12px] px-3 py-3 text-sm font-bold text-[#17383a] transition hover:bg-[#00bbc3]/10 dark:text-white/82 dark:hover:bg-white/8"
                        >
                          {serviceContent}
                        </Link>
                      );
                    })}
                  </motion.div>
                ) : null}
              </AnimatePresence>

              <Link
                to="/about"
                onClick={closeMobileMenu}
                className={`flex h-11 w-full items-center rounded-[10px] px-4 text-sm font-black transition ${
                  isAboutPage
                    ? 'bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] text-white'
                    : 'text-text-primary hover:bg-[#00bbc3]/5 dark:text-white/86 dark:hover:bg-white/8'
                }`}
              >
                {t.common.nav.about}
              </Link>

              <Link
                to="/partners"
                onClick={closeMobileMenu}
                className={`flex h-11 w-full items-center rounded-[10px] px-4 text-sm font-black transition ${
                  isPartnersPage
                    ? 'bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] text-white'
                    : 'text-text-primary hover:bg-[#00bbc3]/5 dark:text-white/86 dark:hover:bg-white/8'
                }`}
              >
                {t.common.nav.partners}
              </Link>

              {middleNavItems.map(item => (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={closeMobileMenu}
                  className={`flex h-11 w-full items-center rounded-[10px] px-4 text-sm font-black transition ${
                    isMiddleNavItemActive(item)
                      ? 'bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] text-white'
                      : 'text-text-primary hover:bg-[#00bbc3]/5 dark:text-white/86 dark:hover:bg-white/8'
                  }`}
                >
                  {item.label}
                </Link>
              ))}

              <Link
                to="/contact"
                onClick={closeMobileMenu}
                className={`flex h-11 w-full items-center rounded-[10px] px-4 text-sm font-black transition ${
                  isContactPage
                    ? 'bg-[#007d9b] dark:bg-gradient-to-r dark:from-[#007d9b] dark:to-[#00bbc3] text-white'
                    : 'text-text-primary hover:bg-[#00bbc3]/5 dark:text-white/86 dark:hover:bg-white/8'
                }`}
              >
                {t.common.nav.contact}
              </Link>

              <div
                className="mt-2 grid grid-cols-3 gap-1 rounded-[10px] bg-background-hover p-1 text-xs font-black text-text-primary dark:bg-white/10 dark:text-white"
                aria-label={t.common.nav.languageSelector}
              >
                {languages.map(languageOption => {
                  const isActiveLanguage = language === languageOption;

                  return (
                    <button
                      key={languageOption}
                      type="button"
                      aria-pressed={isActiveLanguage}
                      title={t.common.languageNames[languageOption]}
                      onClick={() => setLanguage(languageOption)}
                      className={`h-9 cursor-pointer rounded-[8px] transition ${
                        isActiveLanguage
                          ? 'bg-[#007d9b] text-white dark:bg-[#00bbc3] dark:text-[#07142b]'
                          : 'text-text-secondary hover:bg-[#00bbc3]/10 hover:text-[#007d9b] dark:text-white/70 dark:hover:text-[#00bbc3]'
                      }`}
                    >
                      {t.common.languageShortNames[languageOption]}
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={toggleTheme}
                className="mt-2 flex h-11 cursor-pointer items-center justify-between rounded-[10px] bg-background-hover px-4 text-sm font-black text-text-primary dark:bg-white/10 dark:text-white"
              >
                {isDarkTheme ? t.common.nav.darkMode : t.common.nav.lightMode}
                {isDarkTheme ? (
                  <Moon size={18} className="text-[#00bbc3]" />
                ) : (
                  <Sun size={18} className="text-[#00bbc3]" />
                )}
              </button>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.header>
  );
};

export default AppNavbar;
